import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from './supabase';
import { supabaseData } from './supabaseData';

// Default theme values
const defaultTheme = {
  mode: 'dark', // 'dark' or 'light'
  accentColor: '#F05A28', // Orange
  accentColorName: 'orange'
};

// Preset accent colors
export const accentColors = [
  { name: 'orange', color: '#F05A28', gradient: 'linear-gradient(135deg, #F05A28, #f7931e)' },
  { name: 'blue', color: '#3b82f6', gradient: 'linear-gradient(135deg, #3b82f6, #60a5fa)' },
  { name: 'green', color: '#10b981', gradient: 'linear-gradient(135deg, #10b981, #34d399)' },
  { name: 'purple', color: '#8b5cf6', gradient: 'linear-gradient(135deg, #8b5cf6, #a78bfa)' },
  { name: 'red', color: '#ef4444', gradient: 'linear-gradient(135deg, #ef4444, #f87171)' },
  { name: 'teal', color: '#14b8a6', gradient: 'linear-gradient(135deg, #14b8a6, #2dd4bf)' },
  { name: 'pink', color: '#ec4899', gradient: 'linear-gradient(135deg, #ec4899, #f472b6)' },
  { name: 'yellow', color: '#eab308', gradient: 'linear-gradient(135deg, #eab308, #facc15)' }
];

// Theme color schemes
export const getThemeColors = (mode, accentColor) => {
  const accent = accentColors.find(c => c.color === accentColor) || accentColors[0];

  if (mode === 'light') {
    return {
      // Backgrounds
      bg: '#f5f5f5',
      bgCard: '#ffffff',
      bgInput: '#f0f0f0',
      bgHover: '#e8e8e8',

      // Borders
      border: '#e0e0e0',
      borderLight: '#f0f0f0',

      // Text
      text: '#1a1a1a',
      textSecondary: '#666666',
      textMuted: '#888888',

      // Accent
      accent: accent.color,
      accentGradient: accent.gradient,
      accentHover: accent.color + 'dd',

      // Status colors
      success: '#10b981',
      error: '#ef4444',
      warning: '#f59e0b',
      info: '#3b82f6',

      // Sidebar
      sidebarBg: '#ffffff',
      sidebarText: '#1a1a1a',
      sidebarTextMuted: '#666666',
      sidebarHover: '#f0f0f0',
      sidebarActive: accent.color + '15',

      // Shadows
      shadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
      shadowLg: '0 4px 16px rgba(0, 0, 0, 0.12)'
    };
  }

  // Dark mode (default)
  return {
    // Backgrounds
    bg: '#0a0a0a',
    bgCard: '#1a1a1a',
    bgInput: '#2a2a2a',
    bgHover: '#3a3a3a',

    // Borders
    border: '#3a3a3a',
    borderLight: '#2a2a2a',

    // Text
    text: '#e0e0e0',
    textSecondary: '#a0a0a0',
    textMuted: '#888888',

    // Accent
    accent: accent.color,
    accentGradient: accent.gradient,
    accentHover: accent.color + 'cc',

    // Status colors
    success: '#4ecca3',
    error: '#e74c3c',
    warning: '#f39c12',
    info: '#3498db',

    // Sidebar
    sidebarBg: '#1a1a1a',
    sidebarText: '#e0e0e0',
    sidebarTextMuted: '#888888',
    sidebarHover: '#2a2a2a',
    sidebarActive: accent.color + '20',

    // Shadows
    shadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
    shadowLg: '0 4px 16px rgba(0, 0, 0, 0.4)'
  };
};

const ThemeContext = createContext(null);

export function ThemeProvider({ children, companyId }) {
  const [theme, setTheme] = useState(() => {
    // Load from localStorage first for instant display
    const saved = localStorage.getItem('gdh_theme');
    if (saved) {
      try {
        return { ...defaultTheme, ...JSON.parse(saved) };
      } catch (e) {
        return defaultTheme;
      }
    }
    return defaultTheme;
  });

  const colors = getThemeColors(theme.mode, theme.accentColor);

  // Load theme from database when companyId is available
  useEffect(() => {
    if (companyId) {
      loadThemeFromDB();
    }
  }, [companyId]);

  // Apply CSS variables to document root
  useEffect(() => {
    const root = document.documentElement;

    // Set CSS variables for global access
    root.style.setProperty('--bg', colors.bg);
    root.style.setProperty('--bg-card', colors.bgCard);
    root.style.setProperty('--bg-input', colors.bgInput);
    root.style.setProperty('--bg-hover', colors.bgHover);
    root.style.setProperty('--border', colors.border);
    root.style.setProperty('--border-light', colors.borderLight);
    root.style.setProperty('--text', colors.text);
    root.style.setProperty('--text-secondary', colors.textSecondary);
    root.style.setProperty('--text-muted', colors.textMuted);
    root.style.setProperty('--accent', colors.accent);
    root.style.setProperty('--accent-gradient', colors.accentGradient);
    root.style.setProperty('--success', colors.success);
    root.style.setProperty('--error', colors.error);
    root.style.setProperty('--warning', colors.warning);
    root.style.setProperty('--info', colors.info);

    // Set body background
    document.body.style.backgroundColor = colors.bg;
    document.body.style.color = colors.text;

    // Save to localStorage
    localStorage.setItem('gdh_theme', JSON.stringify(theme));
  }, [theme, colors]);

  const loadThemeFromDB = async () => {
    try {
      const { data, error } = await supabaseData
        .from('companies')
        .select('theme_mode, theme_accent_color')
        .eq('id', companyId)
        .single();

      if (!error && data) {
        const dbTheme = {
          mode: data.theme_mode || defaultTheme.mode,
          accentColor: data.theme_accent_color || defaultTheme.accentColor,
          accentColorName: accentColors.find(c => c.color === data.theme_accent_color)?.name || 'orange'
        };
        setTheme(dbTheme);
      }
    } catch (error) {

    }
  };

  const updateTheme = async (updates) => {
    const newTheme = { ...theme, ...updates };

    // Find the color name if changing accent color
    if (updates.accentColor) {
      const found = accentColors.find(c => c.color === updates.accentColor);
      newTheme.accentColorName = found?.name || 'custom';
    }

    setTheme(newTheme);

    // Save to database if companyId available
    if (companyId) {
      try {
        await supabaseData
          .from('companies')
          .update({
            theme_mode: newTheme.mode,
            theme_accent_color: newTheme.accentColor
          })
          .eq('id', companyId);
      } catch (error) {

      }
    }
  };

  const toggleMode = () => {
    updateTheme({ mode: theme.mode === 'dark' ? 'light' : 'dark' });
  };

  const setAccentColor = (color) => {
    updateTheme({ accentColor: color });
  };

  return (
    <ThemeContext.Provider value={{
      theme,
      colors,
      updateTheme,
      toggleMode,
      setAccentColor,
      isDark: theme.mode === 'dark',
      isLight: theme.mode === 'light'
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export default ThemeContext;
